import { gql, type ApolloCache, type FieldPolicy } from "@apollo/client";
import type { MockedResponse } from "@apollo/client/testing";

export const PROFILE_FRAGMENT = gql`
  fragment CoreProfileFields on Profile {
    id
    name
    last_updated
    saml_user_groups
    emails
    users {
      edges {
        node {
          id
          first_name
          last_name
          email
        }
      }
    }
    scopes {
      edges {
        node {
          id
          name
        }
      }
    }
    scope_groups {
      edges {
        node {
          id
          name
          group_type
        }
      }
    }
    statuses {
      edges {
        node {
          key
        }
      }
    }
  }
`;

export const GET_PROFILES = gql`
  ${PROFILE_FRAGMENT}
  query getProfiles {
    profiles {
      edges {
        node {
          ...CoreProfileFields
        }
      }
    }
  }
`;

export const GET_PROFILES_PAGE = gql`
  ${PROFILE_FRAGMENT}
  query getProfilesPage($first: Int, $after: String) {
    profiles(first: $first, after: $after) {
      total_count
      edges {
        node {
          ...CoreProfileFields
        }
      }
    }
  }
`;

// Unnormalized connection: `false` replaces it wholesale (no "cache data may be lost"
// warning) and keeps total_count, which an edges-only merge would drop.
// Keyed by args by default, so the full list and each page are separate entries.
export const profilesCacheFieldPolicy = {
  merge: false,
} as const satisfies FieldPolicy;

// Drops every cached profiles list (full list and pages) so cache-first readers refetch.
export const evictProfilesCache = (cache: ApolloCache<unknown>) => {
  cache.evict({ fieldName: "profiles" });
  cache.gc();
};

export const CNC_GET_PROFILES = gql`
  ${PROFILE_FRAGMENT}
  query cncGetProfiles($account_uuid: String!) {
    cnc_profiles(account_uuid: $account_uuid) {
      edges {
        node {
          ...CoreProfileFields
        }
      }
    }
  }
`;

interface GenerateMockOptions {
  variables?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  error?: Error | null;
}

export const generateProfilesPageData = ({
  variables = null,
  result = null,
  error = null,
}: GenerateMockOptions): MockedResponse => {
  const profilesPageMock: MockedResponse = {
    request: {
      query: GET_PROFILES_PAGE,
      variables: variables ?? undefined,
    },
  };

  if (error) {
    return { ...profilesPageMock, error };
  }

  if (result) {
    return { ...profilesPageMock, result: { data: result } };
  }

  return { ...profilesPageMock, result: {} };
};

export const ADD_PROFILE = gql`
  mutation addProfile($profile: ProfileInput) {
    add_profile(profile: $profile) {
      profile_id
    }
  }
`;

export const generateAddProfile = ({
  variables = null,
  result = null,
  error = null,
}: GenerateMockOptions): MockedResponse => {
  let addProfileMock: MockedResponse = {
    request: {
      query: ADD_PROFILE,
      variables: variables ?? undefined,
    },
  };

  if (error) {
    return {
      ...addProfileMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...addProfileMock,
      result: { data: result },
    };
  }

  return {
    ...addProfileMock,
    result: {},
  };
};

export const EDIT_PROFILE = gql`
  mutation editProfile($profile: ProfileInput, $profile_id: String) {
    edit_profile(profile: $profile, profile_id: $profile_id) {
      profile_id
    }
  }
`;

export const generateEditProfile = ({
  variables = null,
  result = null,
  error = null,
}: GenerateMockOptions): MockedResponse => {
  let editProfileMock: MockedResponse = {
    request: {
      query: EDIT_PROFILE,
      variables: variables ?? undefined,
    },
  };

  if (error) {
    return {
      ...editProfileMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...editProfileMock,
      result: { data: result },
    };
  }

  return {
    ...editProfileMock,
    result: {},
  };
};

export const DELETE_PROFILE = gql`
  mutation deleteProfile($profile_id: String) {
    delete_profile(profile_id: $profile_id) {
      ok
    }
  }
`;

export const generateDeleteProfile = ({
  variables = null,
  result = null,
  error = null,
}: GenerateMockOptions): MockedResponse => {
  let deleteProfileMock: MockedResponse = {
    request: {
      query: DELETE_PROFILE,
      variables: variables ?? undefined,
    },
  };

  if (error) {
    return {
      ...deleteProfileMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...deleteProfileMock,
      result: { data: result },
    };
  }

  return {
    ...deleteProfileMock,
    result: {},
  };
};

export const GET_SCOPE_HIERARCHY_BY_PROFILES = gql`
  query ($profile_ids: [String]) {
    scope_hierarchy_by_profiles(profile_ids: $profile_ids) {
      scope_hierarchy
    }
  }
`;
